import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Badge, Spinner, Select } from 'flowbite-react';
import {
  HiRefresh,
  HiSearch,
  HiInformationCircle,
  HiExclamationCircle,
  HiXCircle,
  HiShieldExclamation,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';

interface LogEvent {
  id: string;
  event_type: string;
  event_category: 'auth' | 'device' | 'mqtt' | 'system' | 'user' | 'security' | 'panic';
  message: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  tenant_id: string | null;
  user_id: string | null;
  device_id: string | null;
  ip_address: string | null;
  created_at: string;
}

export default function AdminLogs() {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  const loadLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('log_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) throw error;
      setLogs(data || []);
      setFilteredLogs(data || []);
    } catch (error: any) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadLogs();
    }
  }, [userProfile]);

  useEffect(() => {
    let filtered = logs;
    if (searchTerm) {
      filtered = filtered.filter(
        (log) =>
          log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.event_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((log) => log.event_category === categoryFilter);
    }
    if (severityFilter !== 'all') {
      filtered = filtered.filter((log) => log.severity === severityFilter);
    }
    setFilteredLogs(filtered);
  }, [searchTerm, categoryFilter, severityFilter, logs]);

  const getSeverityBadge = (severity: string) => {
    const config = {
      info: { color: 'info' as const, icon: HiInformationCircle, label: 'Info' },
      warning: { color: 'warning' as const, icon: HiExclamationCircle, label: 'Advertencia' },
      error: { color: 'failure' as const, icon: HiXCircle, label: 'Error' },
      critical: { color: 'failure' as const, icon: HiShieldExclamation, label: 'Crítico' },
    };
    const severityConfig = config[severity as keyof typeof config] || config.info;
    const Icon = severityConfig.icon;
    return (
      <Badge color={severityConfig.color} icon={Icon}>
        {severityConfig.label}
      </Badge>
    );
  };

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, 'blue' | 'gray' | 'purple' | 'pink' | 'yellow' | 'indigo' | 'red'> = {
      auth: 'blue',
      device: 'gray',
      mqtt: 'purple',
      system: 'pink',
      user: 'yellow',
      security: 'indigo',
      panic: 'red',
    };
    return <Badge color={colors[category] || 'gray'}>{category}</Badge>;
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Logs del Sistema</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Visualiza los eventos y logs del sistema</p>
          </div>
          <Button color="gray" onClick={loadLogs} className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
            <HiRefresh className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <TextInput
                  type="text"
                  placeholder="Buscar en logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
                <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">Todas las categorías</option>
                <option value="auth">Autenticación</option>
                <option value="device">Dispositivo</option>
                <option value="mqtt">MQTT</option>
                <option value="system">Sistema</option>
                <option value="user">Usuario</option>
                <option value="security">Seguridad</option>
                <option value="panic">Pánico</option>
              </Select>
              <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                <option value="all">Todos los niveles</option>
                <option value="info">Info</option>
                <option value="warning">Advertencia</option>
                <option value="error">Error</option>
                <option value="critical">Crítico</option>
              </Select>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron logs</p>
            </div>
          </Card>
        ) : (
          <Card className="bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">Fecha/Hora</th>
                    <th scope="col" className="px-6 py-3">Categoría</th>
                    <th scope="col" className="px-6 py-3">Tipo</th>
                    <th scope="col" className="px-6 py-3">Severidad</th>
                    <th scope="col" className="px-6 py-3">Mensaje</th>
                    <th scope="col" className="px-6 py-3">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('es-ES')}
                      </td>
                      <td className="px-6 py-4">{getCategoryBadge(log.event_category)}</td>
                      <td className="px-6 py-4 font-mono text-xs">{log.event_type}</td>
                      <td className="px-6 py-4">{getSeverityBadge(log.severity)}</td>
                      <td className="px-6 py-4 max-w-md truncate">{log.message}</td>
                      <td className="px-6 py-4 font-mono text-xs">{log.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
